// Epics, tasks and sub-tasks in a flat list. The API returns items in board
// order, parents and children interleaved however their ranks fall; a list
// reads better with each child directly under its parent — an epic's tasks
// under it, each task's sub-tasks under that. Only when both are in the same
// list: a child whose parent is filtered out, or sits in another status group,
// stays where its rank put it, unindented, because indenting it under nothing
// says the wrong thing.

export type Nestable = { id: string; parentTaskId: string | null }

/** Every row exactly once, children right after their parent. `depth` is how
 *  far it is indented: 0, 1 (under a parent in the list) or 2 (under a parent
 *  that is itself under one). */
export function nestChildren<T extends Nestable>(rows: T[]): { task: T; depth: number }[] {
  const present = new Set(rows.map((row) => row.id))
  const childrenOf = new Map<string, T[]>()
  for (const row of rows) {
    if (row.parentTaskId && present.has(row.parentTaskId)) {
      const list = childrenOf.get(row.parentTaskId) ?? []
      list.push(row)
      childrenOf.set(row.parentTaskId, list)
    }
  }

  const out: { task: T; depth: number }[] = []
  const seen = new Set<string>()
  const visit = (row: T, depth: number) => {
    if (seen.has(row.id)) return
    seen.add(row.id)
    out.push({ task: row, depth })
    for (const child of childrenOf.get(row.id) ?? []) visit(child, Math.min(depth + 1, 2))
  }
  for (const row of rows) {
    if (row.parentTaskId && present.has(row.parentTaskId)) continue
    visit(row, 0)
  }
  // A parent cycle can only come from bad data; never lose a row over it.
  for (const row of rows) if (!seen.has(row.id)) visit(row, 0)
  return out
}

export type Laneable = Nestable & {
  level: "epic" | "task" | "subtask"
  epicId?: string | null
  epicKey?: string | null
}

export type Lane<T> = {
  /** The epic's id, or null for the "No epic" lane. */
  epicId: string | null
  epicKey: string | null
  /** The epic itself when it is in the list — the lane header shows it. */
  epic: T | null
  items: T[]
}

/** Swimlanes by epic: one lane per epic, then "No epic" last. The epic's own
 *  card is the lane's header and is not repeated inside it. An epic whose card
 *  is filtered out still gets its lane, named by the key its items carry. */
export function groupByEpic<T extends Laneable>(rows: T[]): Lane<T>[] {
  const lanes = new Map<string, Lane<T>>()
  const lane = (epicId: string, epicKey: string | null): Lane<T> => {
    let found = lanes.get(epicId)
    if (!found) {
      found = { epicId, epicKey, epic: null, items: [] }
      lanes.set(epicId, found)
    }
    return found
  }
  const none: Lane<T> = { epicId: null, epicKey: null, epic: null, items: [] }

  for (const row of rows) {
    if (row.level === "epic") {
      const l = lane(row.id, null)
      l.epic = row
      continue
    }
    if (row.epicId) lane(row.epicId, row.epicKey ?? null).items.push(row)
    else none.items.push(row)
  }
  for (const l of lanes.values()) if (l.epic && "key" in l.epic) l.epicKey = String((l.epic as { key: unknown }).key)
  return none.items.length ? [...lanes.values(), none] : [...lanes.values()]
}
